import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { getFireBaseConfig } from './config/config-select';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// Initialize Firebase.
// Sign-in lives in `src/auth`; content is read/written through the data-access
// layer in `src/data`. Privileged operations clients must not perform directly —
// issuing and redeeming partner invitations — go through the Cloud Functions in
// `functions/`, called via `src/util/callable_functions.ts`.
const firebaseConfig = getFireBaseConfig();
const app = initializeApp(firebaseConfig);

// App Check attests that a request came from our own app before Firestore,
// Storage and the callables will serve it. It has to be initialised before the
// SDKs below, so that they pick up the token provider.
//
// On a dev server we feed it a debug token instead. reCAPTCHA Enterprise will
// not issue a token to `localhost`, and allow-listing localhost on the key is
// not an option: anyone serving a page locally could then mint real tokens with
// the site key out of our bundle. Debug tokens are per-developer and revocable
// — see "App Check" in the README for how to get one.
//
// `import.meta.env.DEV` is false for `vite build`, so none of this survives into
// a deployed bundle.
if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
  (self as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
}

initializeAppCheck(app, {
  provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_APPCHECK_SITE_KEY),
  isTokenAutoRefreshEnabled: true
});

// `ignoreUndefinedProperties` lets us persist experiences straight from the admin
// form: optional fields (legacyPageUrl, timestamps, …) can be `undefined` without
// Firestore throwing, instead of us stripping every undefined key by hand.
const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
const storage = getStorage(app);
const auth = getAuth(app);

// Same region as the /portal project's functions.
const cloudFunctions = getFunctions(app, 'asia-northeast1');

// Point the SDKs at the local emulators (`pnpm emulators`).
//
// Auth is emulated too, and deliberately so: `redeemPartnerInvite` calls
// setCustomUserClaims, so leaving auth pointed at the real project would make
// local testing rewrite claims on real staging accounts. The auth emulator also
// gives us throwaway accounts on any address, which is what makes the
// invitation email-lock testable at all.
if (import.meta.env.VITE_EMULATE_FIREBASE === 'true') {
  console.log('******** Using emulators ********');
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFunctionsEmulator(cloudFunctions, '127.0.0.1', 5001);
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

export { app, auth, cloudFunctions, db, storage };
